import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@helios/auth-firebase-web';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qEncounterFormInstances,
  qPublishedFormDefinitions,
  type FormInstanceListRow,
  type FormDefinitionRow,
} from '@helios/data-access';
import { Drawer } from '@helios/ui/overlay';

// ─── Status Badge ───

function StatusBadge({ status }: { status: string }) {
  const isDraft = status === 'draft';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 'var(--radius-full)',
        background: isDraft ? 'var(--color-warning-bg)' : 'var(--color-success-bg)',
        color: isDraft ? 'var(--color-warning-text)' : 'var(--color-success-text)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '20px',
      }}
    >
      {isDraft ? 'Draft' : 'Completed'}
    </span>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ─── Props ───

interface EncounterFormsTabProps {
  patientId: string;
  encounterId: string;
  visitId?: string | null;
}

export function EncounterFormsTab({ patientId, encounterId }: EncounterFormsTabProps) {
  const navigate = useNavigate();
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const [templateDrawerOpen, setTemplateDrawerOpen] = useState(false);

  const { data: instances = [] } = usePowerSyncQuery<FormInstanceListRow>(
    qEncounterFormInstances(encounterId),
  );

  const { data: publishedForms = [] } = usePowerSyncQuery<FormDefinitionRow>(
    qPublishedFormDefinitions(orgId),
  );

  const handleCreateInstance = useCallback(
    async (formDef: FormDefinitionRow) => {
      if (!db || !orgId) return;
      const id = crypto.randomUUID();
      await db.execute(
        `INSERT INTO form_instances (id, org_id, form_definition_id, patient_id, encounter_id, status, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, 'draft', datetime('now'), datetime('now'))`,
        [id, orgId, formDef.id, patientId, encounterId],
      );
      setTemplateDrawerOpen(false);
      navigate(`/patient/${patientId}/encounters/${encounterId}/forms/${id}`);
    },
    [db, orgId, patientId, encounterId, navigate],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          Forms ({instances.length})
        </span>
        <button
          onClick={() => setTemplateDrawerOpen(true)}
          style={{
            background: primary,
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            padding: '0 14px',
            height: 34,
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: 'var(--font-family)',
          }}
        >
          <NavIcon name="plus" />
          New Form
        </button>
      </div>

      {/* Instance list */}
      {instances.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {instances.map((inst) => (
            <div
              key={inst.id}
              onClick={() =>
                navigate(`/patient/${patientId}/encounters/${encounterId}/forms/${inst.id}`)
              }
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                padding: 'var(--sp-3) var(--sp-4)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                cursor: 'pointer',
                background: 'var(--bg-surface)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-surface)';
              }}
            >
              <NavIcon name="file-text" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  {inst.form_name ?? 'Untitled Form'}
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                    marginTop: 2,
                  }}
                >
                  {formatDate(inst.created_at)}
                </div>
              </div>
              <StatusBadge status={inst.status} />
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            padding: 'var(--sp-8)',
            textAlign: 'center',
            color: 'var(--text-muted)',
            border: '2px dashed var(--border)',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          <div style={{ marginBottom: 'var(--sp-2)' }}>
            <NavIcon name="file-text" />
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-medium)',
              marginBottom: 4,
            }}
          >
            No forms yet
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)' }}>
            Click &quot;New Form&quot; to start filling out a form template.
          </div>
        </div>
      )}

      {/* Template selector drawer */}
      <Drawer
        open={templateDrawerOpen}
        onOpenChange={setTemplateDrawerOpen}
        title="Select Form Template"
      >
        <div
          style={{
            padding: 'var(--sp-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
          }}
        >
          {publishedForms.length === 0 && (
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                textAlign: 'center',
                padding: 'var(--sp-4)',
              }}
            >
              No published form templates. Create and publish templates in Clinic &gt; Forms.
            </p>
          )}
          {publishedForms.map((def) => (
            <button
              key={def.id}
              onClick={() => handleCreateInstance(def)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                padding: 'var(--sp-3) var(--sp-4)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-surface)';
              }}
            >
              <NavIcon name="file-text" />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  {def.name}
                </div>
                {def.description && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                      marginTop: 2,
                    }}
                  >
                    {def.description}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                v{def.version}
              </span>
            </button>
          ))}
        </div>
      </Drawer>
    </div>
  );
}
